import React, { useEffect } from 'react';
import Icon from './icons/Icon';
import { NAV_GROUPS, findSection } from '../config/navigation';

interface SuperAdminNavigationProps {
  activeSection: string;
  activeTab: string;
  /**
   * One callback for both levels. The previous pair of setters meant a section
   * click fired `setActiveSection` then `setActiveTab`, and any consumer that
   * derived the tab from the section saw the two in an inconsistent order.
   */
  onNavigate: (sectionKey: string, tabKey: string) => void;
  /** Collapsed rail shows icons only, for operators who want the width back. */
  isCollapsed?: boolean;
  /** Controls the mobile drawer; ignored at `lg` and above. */
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

/**
 * Primary navigation.
 *
 * Previously this was a horizontal strip of five ALL-CAPS sections with a
 * second strip of up to seven sub-tabs underneath, both of which scrolled
 * sideways below about 1100px — so on a laptop the operator could not see the
 * whole IA at once, and on a phone most of it was off-screen entirely. Two
 * stacked strips also cost ~120px of vertical space on every view, above the
 * numbers.
 *
 * It is now a vertical sidebar: 30 destinations fit down the side without
 * scrolling, the five sections are clustered into three labelled groups
 * instead of reading as five equal peers, and the sub-tabs move next to the
 * content they switch (see SubTabs). Sections render as a disclosure tree so
 * the current section's tabs are visible without hiding the rest of the IA.
 */
const SuperAdminNavigation: React.FC<SuperAdminNavigationProps> = ({
  activeSection,
  activeTab,
  onNavigate,
  isCollapsed = false,
  isMobileOpen = false,
  onMobileClose,
}) => {
  // Escape closes the mobile drawer; without it the only way out on a phone
  // was finding the backdrop.
  useEffect(() => {
    if (!isMobileOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onMobileClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileOpen, onMobileClose]);

  const handleSectionClick = (sectionKey: string) => {
    const section = findSection(sectionKey);
    if (!section) return;
    // Re-clicking the open section returns it to its landing tab rather than
    // doing nothing, which is what people expect from a section header.
    onNavigate(sectionKey, section.defaultTab);
    onMobileClose?.();
  };

  const handleTabClick = (sectionKey: string, tabKey: string) => {
    onNavigate(sectionKey, tabKey);
    onMobileClose?.();
  };

  return (
    <>
      {/* Backdrop, mobile only. */}
      {isMobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onMobileClose}
          className="fixed inset-0 z-30 cursor-default bg-deep-navy/40 lg:hidden"
        />
      ) : null}

      <nav
        aria-label="Console sections"
        className={`fixed inset-y-0 left-0 z-40 flex flex-col overflow-y-auto border-r border-border-subtle bg-surface transition-[width,transform] duration-200 lg:translate-x-0 ${
          isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
        } ${isMobileOpen ? 'translate-x-0 shadow-popover' : '-translate-x-full'}`}
      >
        <div className={`flex h-topbar shrink-0 items-center border-b border-border-subtle ${isCollapsed ? 'justify-center px-2' : 'gap-2.5 px-4'}`}>
          {isCollapsed ? null : (
            <span className="truncate text-sm font-bold tracking-tight text-ink">Icepop Labs</span>
          )}
          {onMobileClose ? (
            <button
              type="button"
              onClick={onMobileClose}
              className="ml-auto rounded-md p-1.5 text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue lg:hidden"
            >
              <Icon name="close" className="h-5 w-5" title="Close navigation" />
            </button>
          ) : null}
        </div>

        <div className="flex-1 space-y-5 py-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              {isCollapsed ? (
                <div aria-hidden="true" className="mx-3 mb-2 border-t border-border-subtle first:border-0" />
              ) : (
                <h2 className="px-4 pb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-3">
                  {group.label}
                </h2>
              )}

              <ul className="space-y-0.5 px-2">
                {group.sections.map((section) => {
                  const isActiveSection = section.key === activeSection;
                  return (
                    <li key={section.key}>
                      <button
                        type="button"
                        onClick={() => handleSectionClick(section.key)}
                        aria-current={isActiveSection ? 'page' : undefined}
                        aria-expanded={isCollapsed ? undefined : isActiveSection}
                        title={isCollapsed ? section.label : undefined}
                        className={`flex w-full items-center rounded-lg px-2.5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue ${
                          isCollapsed ? 'justify-center' : 'gap-2.5'
                        } ${
                          isActiveSection
                            ? 'bg-surface-active text-brand-blue'
                            : 'text-ink-2 hover:bg-surface-hover hover:text-ink'
                        }`}
                      >
                        <Icon name={section.icon} className="h-[18px] w-[18px] shrink-0" />
                        {isCollapsed ? (
                          <span className="sr-only">{section.label}</span>
                        ) : (
                          <>
                            <span className="min-w-0 flex-1 truncate text-left">{section.label}</span>
                            <Icon
                              name="chevronDown"
                              className={`h-4 w-4 shrink-0 text-ink-3 transition-transform ${
                                isActiveSection ? '' : '-rotate-90'
                              }`}
                            />
                          </>
                        )}
                      </button>

                      {/* Sub-tabs stay in the sidebar for the open section so
                          the reader can see where they are in the tree, and
                          jump two levels in one click. */}
                      {!isCollapsed && isActiveSection ? (
                        <ul className="mb-1 ml-[1.4rem] mt-0.5 space-y-px border-l border-border-subtle pl-2">
                          {section.tabs.map((tab) => {
                            const isActiveTab = tab.key === activeTab;
                            return (
                              <li key={tab.key}>
                                <button
                                  type="button"
                                  onClick={() => handleTabClick(section.key, tab.key)}
                                  aria-current={isActiveTab ? 'page' : undefined}
                                  className={`w-full rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue ${
                                    isActiveTab
                                      ? 'bg-info-bg font-semibold text-brand-blue'
                                      : 'text-ink-2 hover:bg-surface-hover hover:text-ink'
                                  }`}
                                >
                                  {tab.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
};

export default SuperAdminNavigation;
